import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { analytics, EVENTS } from "../analytics";
import { useNavigate } from "react-router-dom";

const LandingPageHero: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const description = t("landingPageHero.description");

  return (
    <header className="relative overflow-hidden bg-gradient-to-b from-[#0A1A3E] via-[#11285E] to-[#0A1A3E] text-white py-32 px-6 flex flex-col items-center justify-center">
      {/* Scrolling Background Text */}
      <div className="absolute inset-0 flex items-center overflow-hidden opacity-10 select-none pointer-events-none">
        <motion.div
          initial={{ x: 0 }}
          animate={{ x: [0, -800] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="whitespace-nowrap text-7xl font-extrabold tracking-tight text-white/20"
        >
          BEME CONSULTING • UK CAREER • APPLICATION STRATEGY • BEME CONSULTING •
        </motion.div>
      </div>

      {/* Glow */}
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.25),transparent_70%)]" />

      {/* Orbs */}
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.5, scale: 1 }}
        transition={{ duration: 2 }}
        className="absolute top-16 left-16 w-40 h-40 bg-[#FFDA7A] rounded-full blur-3xl opacity-20"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.5, scale: 1 }}
        transition={{ duration: 2 }}
        className="absolute bottom-16 right-16 w-40 h-40 bg-blue-200 rounded-full blur-3xl opacity-20"
      />

      {/* Main Text */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 text-4xl md:text-6xl font-extrabold text-center max-w-3xl drop-shadow-lg whitespace-pre-line"
      >
        {t("landingPageHero.headline")}
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="relative z-10 mt-6 text-lg md:text-xl text-gray-200 max-w-2xl text-center whitespace-pre-line"
      >
        {t("landingPageHero.subheadline")}
      </motion.p>

      {description && (
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1 }}
          className="relative z-10 mt-4 text-sm md:text-base text-gray-300 max-w-2xl text-center whitespace-pre-line"
        >
          {description}
        </motion.p>
      )}

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2 }}
        className="relative z-10 mt-10"
      >
        <button
          onClick={() => {
            navigate("/service")
            analytics.track(EVENTS.CLICK_HERO_TO_SERVICE)
          }
          }
          className="bg-accent flex items-center gap-2 text-main font-bold px-10 py-4 rounded-lg shadow-md hover:bg-yellow-600 transition-colors"
        >
          {t("landingPageHero.cta")} <ArrowRight size={18} />
        </button>
      </motion.div>
    </header>
  );
};

export default LandingPageHero;
